import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "pits_consent";

// Consent state (a single boolean + timestamp) is stored in localStorage.
// This is the correct storage layer for consent - it must persist across
// sessions per GDPR/POPIA. No sensitive data (emails, IPs, etc.) is stored
// here. If a user clears their browser storage, they simply see the banner
// again on the next visit.
const readConsent = () => {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch (err) {
    if (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[consent] readConsent parse failed", err);
    }
    return null;
  }
};

// Persist and propagate to gtag + fbq
const writeConsent = (granted) => {
  const payload = { granted: !!granted, timestamp: Date.now(), version: 1 };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    if (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[consent] localStorage.setItem failed (likely disabled/private mode)", err);
    }
    // Continue anyway - fall through to session-level consent via gtag/fbq below.
  }
  // GA4 Consent Mode v2 update -----------------------------------------
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: granted ? "granted" : "denied",
      ad_user_data: granted ? "granted" : "denied",
      ad_personalization: granted ? "granted" : "denied",
      analytics_storage: granted ? "granted" : "denied",
    });
  }
  // Meta Pixel consent -------------------------------------------------
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    if (granted) {
      window.fbq("consent", "grant");
      // Fire the initial PageView we deferred until consent was known.
      window.fbq("track", "PageView");
    } else {
      window.fbq("consent", "revoke");
    }
  }
};

export const ConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const current = readConsent();
    if (!current) setVisible(true);
  }, []);

  const accept = () => {
    writeConsent(true);
    setVisible(false);
  };

  const decline = () => {
    writeConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[300] rounded-2xl border border-border bg-card/95 backdrop-blur-xl shadow-[0_25px_80px_rgba(0,0,0,0.55)] p-5 sm:p-6"
    >
      <div className="flex items-start gap-4">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary))/0.12] border border-[hsl(var(--primary))/0.4] text-[hsl(var(--primary))]">
          <Cookie className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display uppercase text-foreground text-sm tracking-[0.02em]">Cookies &amp; analytics</p>
          <p className="mt-2 text-xs sm:text-sm text-muted-foreground leading-relaxed">
            We use cookies for analytics and to improve advertising relevance (Google Analytics, Google Ads, Meta Pixel).
            Nothing is stored until you click Accept. See our{" "}
            <Link to="/privacy" className="text-[hsl(var(--primary))] hover:underline underline-offset-4">privacy policy</Link>.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              data-testid="consent-accept"
              onClick={accept}
              className="h-9 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-4 text-xs uppercase tracking-[0.14em]"
            >
              Accept
            </Button>
            <Button
              data-testid="consent-decline"
              onClick={decline}
              variant="ghost"
              className="h-9 rounded-full border border-border bg-background/60 hover:bg-background px-4 text-xs uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
            >
              Decline
            </Button>
          </div>
        </div>
        <button
          type="button"
          data-testid="consent-close"
          onClick={decline}
          aria-label="Decline cookies"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-background/50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ConsentBanner;
